import {
  activeAmountAt,
  rateConstants,
  type CompoundParams,
  type Dose,
} from "./engine";
import { HOUR_MS, positive, validCadence, validDate } from "./validation";

export type ScheduleVersion = {
  cadenceHours: number;
  startsAt: Date;
  defaultDoseMg: number;
  effectiveAt?: Date;
};
export type Schedule = ScheduleVersion & { history?: ScheduleVersion[] };
export type StoredScheduleVersion = {
  cadenceHours: number;
  startsAt: string;
  defaultDoseMg: number;
  effectiveAt: string;
};

export function validateSchedule(schedule: ScheduleVersion): void {
  validCadence(schedule.cadenceHours);
  positive(schedule.defaultDoseMg, "Default dose");
  validDate(schedule.startsAt, "Schedule anchor");
  if (schedule.effectiveAt)
    validDate(schedule.effectiveAt, "Schedule effective date");
}
export function scheduleVersions(schedule: Schedule): ScheduleVersion[] {
  const versions = [...(schedule.history ?? []), schedule];
  let previous = -Infinity;
  for (const version of versions) {
    validateSchedule(version);
    const effective = +(version.effectiveAt ?? version.startsAt);
    if (effective <= previous)
      throw new Error("Schedule history must have increasing effective dates");
    previous = effective;
  }
  return versions;
}
export function serializeVersion(
  version: ScheduleVersion,
): StoredScheduleVersion {
  validateSchedule(version);
  return {
    cadenceHours: version.cadenceHours,
    defaultDoseMg: version.defaultDoseMg,
    startsAt: version.startsAt.toISOString(),
    effectiveAt: (version.effectiveAt ?? version.startsAt).toISOString(),
  };
}
export function scheduleFromRecord(row: {
  cadenceHours: number;
  defaultDoseMg: number;
  scheduleStartsAt: string | Date;
  scheduleHistory?: unknown;
  currentScheduleEffectiveAt?: string | Date;
}): Schedule {
  if (row.scheduleHistory != null && !Array.isArray(row.scheduleHistory))
    throw new Error("Invalid schedule history");
  const history = ((row.scheduleHistory ?? []) as unknown[]).map(
    (entry): ScheduleVersion => {
      if (!entry || typeof entry !== "object")
        throw new Error("Invalid schedule version");
      const value = entry as Record<string, unknown>;
      if (
        typeof value.cadenceHours !== "number" ||
        typeof value.defaultDoseMg !== "number" ||
        typeof value.startsAt !== "string" ||
        typeof value.effectiveAt !== "string"
      )
        throw new Error("Invalid schedule version");
      return {
        cadenceHours: value.cadenceHours,
        defaultDoseMg: value.defaultDoseMg,
        startsAt: new Date(value.startsAt),
        effectiveAt: new Date(value.effectiveAt),
      };
    },
  );
  const schedule: Schedule = {
    cadenceHours: row.cadenceHours,
    defaultDoseMg: row.defaultDoseMg,
    startsAt: new Date(row.scheduleStartsAt),
    effectiveAt: row.currentScheduleEffectiveAt
      ? new Date(row.currentScheduleEffectiveAt)
      : undefined,
    history,
  };
  scheduleVersions(schedule);
  return schedule;
}
/** A change applies prospectively; old reference doses keep their old amount and phase. */
export function reviseSchedule(
  schedule: Schedule,
  patch: Partial<ScheduleVersion>,
  effectiveAt: Date,
): Schedule {
  scheduleVersions(schedule);
  validDate(effectiveAt, "Change date");
  const next = { ...schedule, ...patch };
  validateSchedule(next);
  if (
    +next.startsAt === +schedule.startsAt &&
    next.cadenceHours === schedule.cadenceHours &&
    next.defaultDoseMg === schedule.defaultDoseMg
  )
    return schedule;
  const { history: _history, ...old } = schedule;
  // A schedule that has not begun has no reference history to preserve.
  const hasBegun = +(schedule.effectiveAt ?? schedule.startsAt) < +effectiveAt;
  const revised: Schedule = {
    ...next,
    effectiveAt,
    history: hasBegun ? [...(schedule.history ?? []), old] : schedule.history,
  };
  scheduleVersions(revised);
  return revised;
}
export function nextScheduledAfter(now: Date, schedule: ScheduleVersion): Date {
  validDate(now);
  validateSchedule(schedule);
  const lower = Math.max(
    +now,
    +(schedule.effectiveAt ?? schedule.startsAt),
    +schedule.startsAt,
  );
  return validDate(
    new Date(
      +schedule.startsAt +
        Math.ceil(
          (lower - +schedule.startsAt) / (schedule.cadenceHours * HOUR_MS),
        ) *
          schedule.cadenceHours *
          HOUR_MS,
    ),
  );
}
function range(evalAt: Date, version: ScheduleVersion, end: number) {
  const first = +nextScheduledAfter(
    version.effectiveAt ?? version.startsAt,
    version,
  );
  const interval = version.cadenceHours * HOUR_MS;
  const last =
    first +
    Math.floor((Math.min(+evalAt, end - 1) - first) / interval) * interval;
  const count = last < first ? 0 : Math.round((last - first) / interval) + 1;
  return { first, last, count, interval, ageHours: (+evalAt - last) / HOUR_MS };
}
/** Exact finite-history sum on the schedule's phase; no arbitrary warm-up truncation. */
export function referenceAmountAt(
  evalAt: Date,
  schedule: Schedule,
  compound: CompoundParams,
): number {
  validDate(evalAt);
  const versions = scheduleVersions(schedule);
  const { ka, ke } = rateConstants(compound);
  let total = 0;
  for (let i = 0; i < versions.length; i++) {
    const version = versions[i]!;
    const next = versions[i + 1];
    const r = range(
      evalAt,
      version,
      next ? +(next.effectiveAt ?? next.startsAt) : Infinity,
    );
    if (!r.count) continue;
    if (Math.abs(ka - ke) < Math.max(ka, ke) * 1e-5) {
      // Stable near the repeated-root limit. Contributions older than 50 half-lives
      // are below floating-point-relevant precision for this bounded cadence.
      const count = Math.min(
        r.count,
        Math.ceil(
          (50 *
            Math.max(
              compound.absorptionHalfLifeHours,
              compound.eliminationHalfLifeHours,
            )) /
            version.cadenceHours,
        ) + 1,
      );
      if (count > 100_000)
        throw new Error("Schedule exceeds the model calculation limit");
      const doses = Array.from({ length: count }, (_, j) => ({
        takenAt: new Date(r.last - j * r.interval),
        amount: version.defaultDoseMg,
      }));
      total += activeAmountAt(evalAt, doses, compound);
    } else {
      const sum = (k: number) =>
        (Math.exp(-k * r.ageHours) *
          -Math.expm1(-k * version.cadenceHours * r.count)) /
        -Math.expm1(-k * version.cadenceHours);
      total +=
        ((compound.bioavailability * version.defaultDoseMg * ka) / (ka - ke)) *
        (sum(ke) - sum(ka));
    }
  }
  if (!Number.isFinite(total))
    throw new Error("Reference calculation is not finite");
  return Math.max(0, total);
}
/** Explicit reference events for inspection and independent numerical tests. */
export function referenceDoses(
  evalAt: Date,
  schedule: Schedule,
  fromTime?: Date,
): Dose[] {
  validDate(evalAt);
  const versions = scheduleVersions(schedule);
  const out: Dose[] = [];
  for (let i = 0; i < versions.length; i++) {
    const version = versions[i]!;
    const next = versions[i + 1];
    const r = range(
      evalAt,
      version,
      next ? +(next.effectiveAt ?? next.startsAt) : Infinity,
    );
    const offset = fromTime
      ? Math.max(0, Math.ceil((+validDate(fromTime) - r.first) / r.interval))
      : 0;
    if (out.length + Math.max(0, r.count - offset) > 100_000)
      throw new Error("Too many reference events to enumerate");
    for (let j = offset; j < r.count; j++)
      out.push({
        takenAt: new Date(r.first + j * r.interval),
        amount: version.defaultDoseMg,
      });
  }
  return out;
}
